import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchFaqsNotesDocuments } from 'src/store/actions/faqsNotesDocuments';
import { ICONS } from 'src/common/constants';
import { fetchAllIssueTypes } from 'src/store/actions/formOptions';
import { selectServiceRequestFormData } from 'src/store/selectors/serviceRequestForm';
import {
  setServiceRequestFormData,
  setServiceRequestFormNextStep,
  setServiceRequestFormPreviousStep,
  exitServiceRequestForm,
  setServiceRequestFormStep,
} from 'src/store/actions/serviceRequestForm';
import Button from '../Button';
import Col from '../Col';
import Diagnosis from '../Diagnosis/Diagnosis';
import Document from '../Document';
import Form from '../Form';
import Icon from '../Icon';
import Progress from './Progress';
import Row from '../Row';
import { selectCommonAreaAssets } from 'src/store/selectors/commonAreaAssets';
import { selectAssets } from 'src/store/selectors/assets';
import { toast } from 'react-toastify';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import Loader from '../Loader';
import { onUrlImageExtensionExist } from 'src/helpers/CommonHelpers';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import './ServiceRequestFormAsset.scss';

interface AssetItemProps {
  asset: any;
  index: any;
  serviceRequestFormData: any;
  onSetServiceRequestFormData: any;
  onFetchFaqsNotesDocuments: any;
  user: any;
  filterAssetsFaqData: any;
}

// Separate Asset rendering component.
const AssetItem = ({
  asset,
  index,
  serviceRequestFormData,
  onSetServiceRequestFormData,
  onFetchFaqsNotesDocuments,
  user,
  filterAssetsFaqData,
}: AssetItemProps) => {
  return (
    <Col
      key={index}
      xs="6"
      sm="4"
      style={{
        flexShrink: 1,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {asset.item !== null ? (
        <Document
          active={serviceRequestFormData.assetId === asset.id}
          adaptiveSize
          onClick={() => {
            const expiryDate = new Date(asset.warrantExpiration);
            const today = new Date();
            const differenceInTime = expiryDate.getTime() - today.getTime();
            const differenceInDays = differenceInTime / (1000 * 3600 * 24);
            const inOneYeardaysRemaining = differenceInDays - 1;
            if (
              inOneYeardaysRemaining < 0 &&
              (user.roles[0].role.name === 'Home Owner' ||
                user.roles[0].role.name === 'Occupant')
            ) {
              toast.error(
                ` ⓘ We're sorry, but ${
                  asset.item && asset.item.name
                    ? asset.item.name
                    : 'Selected Asset'
                } is currently past your warranty end date. Please contact us should you have any questions or concerns regarding your warranty.`,
                { autoClose: 6000, position: 'top-center' }
              );
            } else {
              onSetServiceRequestFormData({
                asset_serial_number: asset.serialNumber,
                asset_item_make:
                  asset.item && asset.item.makeName ? asset.item.makeName : '',
                asset_item_model:
                  asset.item && asset.item.model ? asset.item.model : '',
                asset_item_name:
                  asset.item &&
                  asset.item.itemTypeIcons &&
                  asset.item.itemTypeIcons.name
                    ? asset.item.itemTypeIcons.name
                    : '',
                asset_item_nameDescription:
                  asset.item && asset.item.name ? asset.item.name : '',
                asset_item_type: null,
                asset_item_id: asset.item && asset.item.id ? asset.item.id : '',
                assetId: asset.id,
                itemId: asset.item && asset.item.id ? asset.item.id : '',
                assetWarrantyExpiresAt:
                  asset.warrantExpiration && asset.warrantExpiration
                    ? asset.warrantExpiration
                    : '',
                annotatateImage: false,
              });
              if (asset.item && asset.item.id) {
                onFetchFaqsNotesDocuments(asset.item.id);
                filterAssetsFaqData();
              }
            }
          }}
          headline={asset.item && asset.item.name ? asset.item.name : ''}
          icon={
            <Icon
              glyph={
                ICONS[
                  asset.item &&
                  asset.item.itemTypeIcons &&
                  asset.item.itemTypeIcons.name
                    ? asset.item.itemTypeIcons.name
                    : ''
                ]
              }
              weight="light"
            />
          }
          strapline={undefined}
          itemName={undefined}
          itemTypeWalkthrough={undefined}
          image={undefined}
          onDownload={undefined}
          small={undefined}
          onView={undefined}
          onDelete={undefined}
          check={undefined}
          edit={undefined}
          onEdit={undefined}
        />
      ) : null}
    </Col>
  );
};

interface RoomAssetsProps {
  rooms: any;
  room: any;
  serviceRequestFormData: any;
  onSetServiceRequestFormData: any;
  onFetchFaqsNotesDocuments: any;
  user: any;
  filterAssetsFaqData: any;
}

// Separate Room and Assets rendering component
const RoomAssets = ({
  rooms,
  room,
  serviceRequestFormData,
  onSetServiceRequestFormData,
  onFetchFaqsNotesDocuments,
  user,
  filterAssetsFaqData,
}: RoomAssetsProps) => {
  if (serviceRequestFormData.viewAsset !== room.name) {
    return null;
  }

  const filterRooms: any = rooms.filter((a: any) => a.item !== null);
  const uniqueRooms: any =
    filterRooms.length > 0
      ? [
          ...new Map(
            filterRooms.map((item: any) => [item.item.name, item])
          ).values(),
        ]
      : rooms;

  return (
    <>
      {uniqueRooms.map((asset, x) => (
        <AssetItem
          key={x}
          asset={asset}
          index={x}
          serviceRequestFormData={serviceRequestFormData}
          onSetServiceRequestFormData={onSetServiceRequestFormData}
          onFetchFaqsNotesDocuments={onFetchFaqsNotesDocuments}
          user={user}
          filterAssetsFaqData={filterAssetsFaqData}
        />
      ))}
    </>
  );
};

interface ServiceRequestFormAssetProps {
  onExit?: any;
}

// Main Component.
const ServiceRequestFormAsset = ({ onExit }: ServiceRequestFormAssetProps) => {
  const [state, setState] = useState({
    showOverlay: false,
    faqs: [],
    loading: false,
    showLightBox: false,
  });

  const dispatch = useDispatch();

  // Convert mapStateToProps to useSelector hooks.
  const serviceRequestFormData: any = useSelector(selectServiceRequestFormData);
  const assets: any = useSelector(selectAssets);
  const commonAreaAssets: any = useSelector(selectCommonAreaAssets);
  const user: any = useSelector(selectAuthenticatedUser);
  const faqsNotesDocuments: any = useSelector(
    (state: any) => state.faqsNotesDocuments
  );
  const types: any = useSelector((state: any) => state.formOptions.types);

  // Convert mapDispatchToProps to dispatch calls.
  const onFetchIssueTypes = () => dispatch(fetchAllIssueTypes());
  const onSetServiceRequestFormData = (data: any) =>
    dispatch(setServiceRequestFormData(data));
  const onSetServiceRequestFormNextStep = () =>
    dispatch(setServiceRequestFormNextStep());
  const onSetServiceRequestFormPreviousStep = () =>
    dispatch(setServiceRequestFormPreviousStep());
  const onExitServiceRequestForm = () => dispatch(exitServiceRequestForm());
  const onFetchFaqsNotesDocuments = (id: any) =>
    dispatch(fetchFaqsNotesDocuments(id));
  const onSetServiceRequestFormStep = (step: any) =>
    dispatch(setServiceRequestFormStep(step));

  useEffect(() => {
    onFetchIssueTypes();
    if (
      serviceRequestFormData &&
      !serviceRequestFormData.issueType &&
      !serviceRequestFormData.issueTypeId
    ) {
      let issueType = types.filter((a) => a.name === 'General')[0];
      onSetServiceRequestFormData({
        issueType: issueType.name,
        issueTypeId: issueType.id,
      });
    }
  }, []); // Empty dependency array to mimic componentDidMount.

  const filterAssetsFaqData = () => {
    setState((prevState: any) => ({ ...prevState, loading: true }));
    setTimeout(() => {
      let faqs =
        faqsNotesDocuments.length > 0
          ? faqsNotesDocuments.filter((item: any) => item.type === 'FAQS')
          : [];
      setState((prevState: any) => ({
        ...prevState,
        faqs: faqs,
        loading: false,
      }));
    }, 2000);
  };

  const modifiedCommonAsset: any =
    commonAreaAssets && commonAreaAssets.length > 0
      ? [
          {
            name: commonAreaAssets[0].roomType,
            assets: commonAreaAssets,
          },
        ]
      : [];
  const commonAssets: any =
    serviceRequestFormData.serviceRequestType === 'Common Area'
      ? modifiedCommonAsset
      : assets;

  return (
    <Form
      headline="Let's Identify the Issue"
      strapline="Tell us what's happening and we'll be able to find the right solution to get things back to normal."
      onClose={onSetServiceRequestFormPreviousStep}
      onExit={() => {
        onExitServiceRequestForm();
        if (onExit) onExit();
      }}
      component={
        <Row mdCenter>
          <Col xs="12" md="6">
            <Diagnosis
              asset={serviceRequestFormData.asset_item_nameDescription}
              issueType={
                user.roles[0].role.name === 'Home Owner'
                  ? serviceRequestFormData.issueType
                  : serviceRequestFormData.asset_item_type
              }
              expiresAt={serviceRequestFormData.assetWarrantyExpiresAt}
              location={undefined}
              label={undefined}
            />
            <Progress currentStep={0} totalSteps={3} />
            {serviceRequestFormData.suiteFloorPLan &&
              serviceRequestFormData.suiteFloorPLan.length > 0 &&
              onUrlImageExtensionExist(
                serviceRequestFormData.suiteFloorPLan
              ) === true && (
                <div
                  className="service-request-from-asset__title"
                  style={{
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    textAlign: 'right',
                  }}
                  onClick={() =>
                    setState((prevState) => ({
                      ...prevState,
                      showLightBox: true,
                    }))
                  }
                >
                  View Floorplan
                </div>
              )}
            {serviceRequestFormData.suiteFloorPLan &&
              serviceRequestFormData.suiteFloorPLan.length > 0 &&
              onUrlImageExtensionExist(
                serviceRequestFormData.suiteFloorPLan
              ) === false && (
                <div
                  className="service-request-from-asset__title"
                  style={{
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    textAlign: 'right',
                  }}
                  onClick={() =>
                    window.open(serviceRequestFormData.suiteFloorPLan, '_blank')
                  }
                >
                  View Floorplan
                </div>
              )}
            {state.showLightBox === true ? (
              <ReusableLightbox
                isOpen={state.showLightBox}
                onClose={() =>
                  setState((prevState) => ({
                    ...prevState,
                    showLightBox: false,
                  }))
                }
                image={serviceRequestFormData.suiteFloorPLan}
              />
            ) : null}
            {commonAssets.map((room) => (
              <div key={room.name}>
                <div
                  style={{
                    display: 'flex',
                    textAlign: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    onSetServiceRequestFormData({
                      viewAsset:
                        serviceRequestFormData.viewAsset === room.name
                          ? ''
                          : room.name,
                    })
                  }
                >
                  <div
                    className="service-request-from-asset__title"
                    style={{ cursor: 'pointer' }}
                  >
                    {room.customLabel ? room.customLabel : room.name}
                  </div>
                  <Icon
                    glyph={
                      serviceRequestFormData.viewAsset === room.name
                        ? 'chevron-up'
                        : 'chevron-down'
                    }
                    weight="regular"
                  />
                </div>
                <Row>
                  <RoomAssets
                    rooms={room.assets}
                    room={room}
                    serviceRequestFormData={serviceRequestFormData}
                    onSetServiceRequestFormData={onSetServiceRequestFormData}
                    onFetchFaqsNotesDocuments={onFetchFaqsNotesDocuments}
                    user={user}
                    filterAssetsFaqData={filterAssetsFaqData}
                  />
                </Row>
              </div>
            ))}
          </Col>
        </Row>
      }
      footer={
        <div
          style={{
            position: 'fixed',
            bottom: '0',
            left: '0',
            width: '100%',
            background: '#fff',
            padding: '15px',
            boxShadow: '0 0 0.3rem rgba(0, 0, 0, 0.3)',
          }}
        >
          <>
            {state.loading ? <Loader center={true} /> : null}
            {user.roles[0].role.name === 'Home Owner' ? (
              state.faqs.length > 0 ? (
                <Button
                  color="teal"
                  size="small"
                  label="Next: Common Questions"
                  onClick={() => onSetServiceRequestFormStep(3)}
                  block={undefined}
                  disabled={undefined}
                  outlined={undefined}
                  icon={undefined}
                  outline={undefined}
                  weight={undefined}
                  children={undefined}
                  style={undefined}
                />
              ) : (
                <Button
                  color="teal"
                  size="small"
                  label="Next: Add Details"
                  onClick={() => onSetServiceRequestFormStep(6)}
                  disabled={!serviceRequestFormData.asset_item_id}
                  block={undefined}
                  outlined={undefined}
                  icon={undefined}
                  outline={undefined}
                  weight={undefined}
                  children={undefined}
                  style={undefined}
                />
              )
            ) : (
              <Button
                color="teal"
                size="small"
                label="Next: Identify Issue"
                onClick={() => onSetServiceRequestFormNextStep()}
                disabled={!serviceRequestFormData.asset_item_id}
                block={undefined}
                outlined={undefined}
                icon={undefined}
                outline={undefined}
                weight={undefined}
                children={undefined}
                style={undefined}
              />
            )}
          </>
        </div>
      }
      actions={undefined}
      onSubmit={undefined}
    />
  );
};

export default ServiceRequestFormAsset;
